/**
 * Episode Durations List View
 *
 * Read-only episode durations display with localized names and descriptions.
 * Episode durations are managed via database migrations, not through the admin UI.
 * Includes language filter to view localizations in different languages.
 *
 * Story 15.11: Categorical Duration
 */

import { useState, useMemo } from 'react';
import { DictionariesNavigation } from '../components/DictionariesNavigation';
import { useInitialData, type EpisodeDuration } from '../../../contexts/InitialDataContext';

/**
 * Get localized name for an episode duration
 * Falls back to English if requested language not available
 */
function getEpisodeDurationLocalizedName(duration: EpisodeDuration, languageCode: string): string {
  // Direct lookup by language code (dict format)
  if (duration.localizations[languageCode]) {
    return duration.localizations[languageCode].name;
  }

  // Fallback to English
  if (duration.localizations['en']) {
    return duration.localizations['en'].name;
  }

  // Fallback to first available
  const firstLoc = Object.values(duration.localizations)[0];
  if (firstLoc) {
    return firstLoc.name;
  }

  return `Duration ${duration.id}`;
}

/**
 * Get localized description for an episode duration
 * Falls back to English if requested language not available
 */
function getEpisodeDurationLocalizedDescription(
  duration: EpisodeDuration,
  languageCode: string
): string | undefined {
  // Direct lookup by language code (dict format)
  if (duration.localizations[languageCode]) {
    return duration.localizations[languageCode].description;
  }

  // Fallback to English
  if (duration.localizations['en']) {
    return duration.localizations['en'].description;
  }

  // Fallback to first available
  const firstLoc = Object.values(duration.localizations)[0];
  if (firstLoc) {
    return firstLoc.description;
  }

  return undefined;
}

/**
 * Check if episode duration has localization for given language
 */
function hasLocalization(duration: EpisodeDuration, languageCode: string): boolean {
  return languageCode in duration.localizations;
}

export function EpisodeDurationsListView() {
  const [displayLanguage, setDisplayLanguage] = useState<string>('en');

  const { state: initialDataState } = useInitialData();

  const languages = initialDataState.data?.languages ?? [];

  // Sort episode durations by display_order
  const sortedDurations = useMemo(() => {
    const episodeDurations = initialDataState.data?.episodeDurations ?? [];
    return [...episodeDurations].sort((a, b) => a.display_order - b.display_order);
  }, [initialDataState.data?.episodeDurations]);

  if (initialDataState.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (initialDataState.error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading episode durations</p>
          <p className="text-gray-600">{initialDataState.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <DictionariesNavigation />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Episode Durations</h1>
          <p className="text-gray-600 mt-2">
            Categorical episode durations (Short/Medium/Long/Extended) with turn counts and
            localized names. Select a language to view localizations.
          </p>
        </div>
      </div>

      {/* Language Filter */}
      <div className="mb-6 flex gap-4">
        <div className="w-48">
          <label
            htmlFor="display-language"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Display Language
          </label>
          <select
            id="display-language"
            value={displayLanguage}
            onChange={(e) => setDisplayLanguage(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            data-testid="display-language-selector"
          >
            {languages
              .filter((lang) => lang.code !== 'zxx')
              .map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
          </select>
        </div>
      </div>

      {/* Episode Durations Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table
          className="min-w-full divide-y divide-gray-200"
          data-testid="episode-durations-table"
        >
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Turns
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Available Localizations
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedDurations.map((duration) => {
              const hasLoc = hasLocalization(duration, displayLanguage);
              // Get sorted language codes for badge display
              const sortedLanguageCodes = Object.keys(duration.localizations).sort();
              return (
                <tr key={duration.id} data-testid={`episode-duration-row-${duration.id}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{duration.id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {duration.max_turns === 999 ? '>50' : `≤${duration.max_turns}`}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm ${hasLoc ? 'text-gray-900' : 'text-gray-500 italic'}`}>
                      {getEpisodeDurationLocalizedName(duration, displayLanguage)}
                      {!hasLoc && ' (fallback)'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm ${hasLoc ? 'text-gray-600' : 'text-gray-400 italic'}`}>
                      {getEpisodeDurationLocalizedDescription(duration, displayLanguage) || (
                        <span className="italic text-gray-400">No description</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {sortedLanguageCodes.map((langCode) => (
                        <span
                          key={langCode}
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            langCode === displayLanguage
                              ? 'bg-blue-100 text-blue-800 ring-1 ring-blue-500'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                          title={duration.localizations[langCode].name}
                        >
                          {langCode.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {sortedDurations.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center" data-testid="empty-state">
          <p className="text-gray-600 text-lg">No episode durations configured in the system.</p>
        </div>
      )}
    </div>
  );
}
